/**
 * useCardReissue — Debit-card re-issue cascade (block + add-new).
 *
 * Legacy procedure references:
 *   - `New Card in Core Director.pdf`
 *   - Entrust series (printing, re-PIN, moving cards)
 *
 * Flow:
 *   1. Block existing card: EftCardMod with Status='Blocked' (or 'Replaced'
 *      when reason is Lost/Stolen → block reason code mirrors the reason).
 *      This step is skipped if cardNumber is empty (card already inactive).
 *   2. Issue new card: EftCardAdd with design, mailTo preference.
 *   3. Audit note (CustMsgAdd).
 *   4. Session events: acct.card.blocked (if blocked), acct.card.reissued.
 *   5. Auto-task if MailTo='Home': "Card mailed — confirm with customer"
 *      (priority 4).
 */

import { useCallback } from 'react';
import type { EftCardAddRequest, EftCardModRequest } from '@retail-ops/shared-types';
import type { AcctType } from '@retail-ops/shared-types/enums';
import { useApi } from '../../hooks/useApi';
import { useEmitEvent } from '../../hooks/useEmitEvent';
import type { MaintActionResult } from '../types';

export type CardReissueReason = 'Lost' | 'Stolen' | 'Damaged' | 'Upgrade' | 'NameChange';

export interface CardReissueInput {
  cifId: string;
  acctId: string;
  acctType: AcctType | string;
  /** Existing card number to block. Empty = skip block. */
  existingCardNumber?: string;
  reason: CardReissueReason;
  /** Where the new card ships. */
  mailTo: 'Home' | 'Branch';
  /** Card design code from ao_CardDesigns (config). */
  cardDesign?: string;
  /** Required when reason is Lost/Stolen. */
  blockExisting: boolean;
  officerCode: string;
}

export interface CardReissueResult extends MaintActionResult {
  newCardLast4?: string;
}

export interface UseCardReissueApi {
  run: (input: CardReissueInput) => Promise<CardReissueResult>;
}

function blockReasonFor(reason: CardReissueReason): string {
  switch (reason) {
    case 'Lost':       return 'LOST';
    case 'Stolen':     return 'STOLEN';
    case 'Damaged':    return 'DAMAGED';
    case 'Upgrade':    return 'UPGRADE';
    case 'NameChange': return 'NAMECHG';
    default:           return 'OTHER';
  }
}

export function useCardReissue(): UseCardReissueApi {
  const { client } = useApi();
  const emit = useEmitEvent();

  const run = useCallback(
    async (input: CardReissueInput): Promise<CardReissueResult> => {
      try {
        // ------------------------------------------------------------------
        // 1. Block existing card (if applicable)
        // ------------------------------------------------------------------
        if (input.blockExisting && input.existingCardNumber) {
          const blockReq: EftCardModRequest = {
            CifId: input.cifId,
            CardNumber: input.existingCardNumber,
            Status: input.reason === 'Upgrade' || input.reason === 'NameChange' ? 'Replaced' : 'Blocked',
            BlockReason: blockReasonFor(input.reason),
            OperatorNumber: input.officerCode,
          };
          await client.accounts.eftCardMod(blockReq);
          emit({
            eventType: 'acct.card.blocked',
            eventCategory: 'cif-account',
            payload: {
              cifId: input.cifId,
              acctId: input.acctId,
              cardNumber: input.existingCardNumber,
              reason: input.reason,
            },
          });
        }

        // ------------------------------------------------------------------
        // 2. Issue new card
        // ------------------------------------------------------------------
        const addReq: EftCardAddRequest = {
          CifId: input.cifId,
          AcctId: input.acctId,
          AcctType: input.acctType as AcctType,
          CardType: 'Debit',
          ...(input.cardDesign ? { CardDesign: input.cardDesign } : {}),
          MailTo: input.mailTo,
          OperatorNumber: input.officerCode,
        };
        const addResp = await client.accounts.eftCardAdd(addReq);
        if (!addResp.success) {
          return {
            ok: false,
            error: addResp.error ?? 'EftCardAdd failed',
          };
        }

        // ------------------------------------------------------------------
        // 3. Audit note
        // ------------------------------------------------------------------
        const today = new Date().toISOString().slice(0, 10);
        const auditNote =
          `Debit card re-issued on ${today} (reason ${input.reason}) — ` +
          `ship to ${input.mailTo}` +
          (addResp.cardLast4 ? `, new last4 ${addResp.cardLast4}` : '') +
          ` — officer ${input.officerCode}.`;
        try {
          await client.jxrl.post('Customer/CustMsgAdd', {
            CustId: input.cifId,
            MsgType: 'AN',
            Msg: auditNote,
            OperatorNumber: input.officerCode,
          });
        } catch {
          /* non-fatal */
        }

        emit({
          eventType: 'acct.card.reissued',
          eventCategory: 'cif-account',
          payload: {
            cifId: input.cifId,
            acctId: input.acctId,
            reason: input.reason,
            mailTo: input.mailTo,
            newCardLast4: addResp.cardLast4,
          },
        });

        // ------------------------------------------------------------------
        // 4. Auto-task if mailed to home
        // ------------------------------------------------------------------
        if (input.mailTo === 'Home') {
          try {
            await client.retail.tasks.create({
              title: 'Debit card mailed — confirm receipt with customer',
              type: 'card-mailed-followup',
              category: 'workflow',
              priority: 'Low',
              assignedTo: input.officerCode,
              cifId: input.cifId,
              severity: 'info',
              autoCreated: true,
              description:
                `New debit card (reason ${input.reason}) mailed to customer home on ` +
                `${today}. Follow up in 7-10 business days if not received.`,
            });
          } catch {
            /* non-fatal */
          }
        }

        // TODO: wire disclosure packet when Team 2's engine lands
        //       (Temp-card letter for Lost/Stolen; name-change attestation).

        return {
          ok: true,
          message: `New card issued (${input.mailTo}).`,
          ...(addResp.cardLast4 ? { newCardLast4: addResp.cardLast4 } : {}),
        };
      } catch (err) {
        return { ok: false, error: err instanceof Error ? err.message : String(err) };
      }
    },
    [client, emit],
  );

  return { run };
}
